import { BitkitIconComponent, IconBitbucket, IconGit, IconGithub, IconGitlab } from '@bitrise/bitkit-v2';

enum GitProvider {
  GITHUB = 'github',
  GITHUB_APP = 'github-app',
  GITHUB_EC_DR = 'github-ec-dr',
  GITHUB_SELF_HOSTED = 'github-self-hosted',
  GITLAB = 'gitlab',
  GITLAB_SELF_HOSTED = 'gitlab-self-hosted',
  GITSTREAM = 'gitstream',
  BITBUCKET = 'bitbucket',
  BITBUCKET_SERVER = 'bitbucket-server',
  CUSTOM = 'custom',
}

export type GitProviderId = `${GitProvider}`;

export const getGitProviderName = (id: GitProvider | undefined) => {
  switch (id) {
    case GitProvider.GITHUB:
    case GitProvider.GITHUB_APP:
      return 'GitHub';
    case GitProvider.GITHUB_SELF_HOSTED:
      return 'GitHub Enterprise Server';
    case GitProvider.GITHUB_EC_DR:
      return 'GitHub Enterprise Cloud with data residency';
    case GitProvider.GITLAB:
      return 'GitLab';
    case GitProvider.GITLAB_SELF_HOSTED:
      return 'Self-hosted GitLab';
    case GitProvider.GITSTREAM:
      return 'Gitstream';
    case GitProvider.BITBUCKET:
      return 'Bitbucket';
    case GitProvider.BITBUCKET_SERVER:
      return 'Bitbucket Server';
    case GitProvider.CUSTOM:
    default:
      return 'Git provider';
  }
};

export const getGitProviderIcon = (id: GitProvider): BitkitIconComponent => {
  switch (id) {
    case GitProvider.GITHUB:
    case GitProvider.GITHUB_APP:
    case GitProvider.GITHUB_SELF_HOSTED:
    case GitProvider.GITHUB_EC_DR:
      return IconGithub;
    case GitProvider.GITLAB:
    case GitProvider.GITLAB_SELF_HOSTED:
      return IconGitlab;
    case GitProvider.BITBUCKET:
    case GitProvider.BITBUCKET_SERVER:
      return IconBitbucket;
    case GitProvider.GITSTREAM:
    case GitProvider.CUSTOM:
    default:
      return IconGit;
  }
};

const SCUSupportedProviders = [
  GitProvider.GITHUB,
  GitProvider.GITLAB,
  GitProvider.BITBUCKET,
  GitProvider.GITLAB_SELF_HOSTED,
  GitProvider.BITBUCKET_SERVER,
];

const GHESSupportedProviders = [GitProvider.GITHUB_SELF_HOSTED, GitProvider.GITHUB_EC_DR, GitProvider.CUSTOM];

// Mirrors the provider list in Repository#webhook_auto_reg_supported?, the only clients that implement
// setup_webhook. GITHUB_APP is absent because it handles webhooks through the installation instead.
const WebhookAutoRegSupportedProviders: GitProviderId[] = [
  GitProvider.GITHUB,
  GitProvider.GITLAB,
  GitProvider.BITBUCKET,
  GitProvider.GITLAB_SELF_HOSTED,
  GitProvider.BITBUCKET_SERVER,
];

// GITHUB_SELF_HOSTED is absent: those projects use a manually registered webhook, so the backend doesn't forward
// their GitHub App's events, which would start every build twice.
const WebhookManagedByGitHubAppProviders: GitProviderId[] = [GitProvider.GITHUB_APP, GitProvider.GITHUB_EC_DR];

export const isProviderSCUSupported = (id: GitProvider | undefined): boolean =>
  id ? SCUSupportedProviders.includes(id) : false;
export const isProviderWebhookAutoRegSupported = (id: GitProviderId | undefined): boolean =>
  id ? WebhookAutoRegSupportedProviders.includes(id) : false;
export const isProviderGHESSupported = (id: GitProvider | undefined): boolean =>
  id ? GHESSupportedProviders.includes(id) : false;
export const isProviderWebhookManagedByGitHubApp = (id: GitProviderId | undefined): boolean =>
  id ? WebhookManagedByGitHubAppProviders.includes(id) : false;

export const getGitProviderPatUrl = (id: GitProvider | undefined): string | undefined => {
  switch (id) {
    case GitProvider.GITHUB:
      return 'https://github.com/settings/tokens?type=beta';
    case GitProvider.GITLAB:
      return 'https://gitlab.com/-/profile/personal_access_tokens';
    case GitProvider.BITBUCKET:
      return 'https://id.atlassian.com/manage-profile/security/api-tokens';
    default:
      return undefined;
  }
};

export type GitProviderPatScope = {
  /** The exact scope/permission identifier the user must select during PAT creation. */
  scope: string;
  /** Human-readable explanation shown on hover. */
  description: string;
};

/**
 * The minimum token scopes a user must grant when creating a Personal Access Token (PAT) for Bitrise.
 * We can't deep-link to the provider's token-creation page with these pre-filled, so we surface them
 * in the Authorize step instead. Returns an empty list for providers without a documented cloud PAT
 * flow (e.g. self-hosted), in which case the scopes hint is not rendered.
 */
export const getGitProviderPatScopes = (id: GitProvider | undefined): GitProviderPatScope[] => {
  switch (id) {
    case GitProvider.GITHUB:
      return [
        {
          scope: 'repo',
          description: 'Clone your repository and report build statuses back to commits and pull requests.',
        },
        {
          scope: 'admin:repo_hook',
          description: 'Create and manage webhooks so builds can be triggered automatically.',
        },
        { scope: 'read:user', description: 'Read your basic profile information to identify your account.' },
      ];
    case GitProvider.GITLAB:
      return [
        { scope: 'api', description: 'Register webhooks and report build statuses through the GitLab API.' },
        { scope: 'read_repository', description: 'Clone your repository so Bitrise can build your code.' },
      ];
    case GitProvider.BITBUCKET:
      return [
        { scope: 'read:repository:bitbucket', description: 'Clone your repository so Bitrise can build your code.' },
        { scope: 'read:user:bitbucket', description: 'Read your basic account information to identify your account.' },
        { scope: 'read:workspace:bitbucket', description: 'Read the workspace your repository belongs to.' },
        {
          scope: 'read:pullrequest:bitbucket',
          description: 'Read pull requests so Bitrise can trigger and report builds.',
        },
        { scope: 'read:webhook:bitbucket', description: 'Read existing webhooks on the repository.' },
        {
          scope: 'write:webhook:bitbucket',
          description: 'Create and manage webhooks so builds can be triggered automatically.',
        },
      ];
    default:
      return [];
  }
};

export const getGitProviderSshKeyUrl = (id: GitProvider | undefined): string | undefined => {
  switch (id) {
    case GitProvider.GITHUB:
      return 'https://github.com/settings/keys';
    case GitProvider.GITLAB:
      return 'https://gitlab.com/-/profile/keys';
    case GitProvider.BITBUCKET:
      return 'https://bitbucket.org/account/settings/ssh-keys/';
    default:
      return undefined;
  }
};

const HTTP_PAT_ONLY_PROVIDERS: GitProvider[] = [GitProvider.GITHUB, GitProvider.GITLAB];
export const requiresHttpUsername = (id: GitProvider | undefined): boolean =>
  id === undefined || !HTTP_PAT_ONLY_PROVIDERS.includes(id);

export const getGitProviderPullRequestUrl = (
  id?: GitProvider,
  owner?: string,
  repoSlug?: string,
  pullRequestId?: number,
): string | undefined => {
  if (!id || !owner || !repoSlug || !pullRequestId) {
    return undefined;
  }

  switch (id) {
    case GitProvider.GITHUB:
    case GitProvider.GITHUB_APP:
      return `https://github.com/${owner}/${repoSlug}/pull/${pullRequestId}`;
    case GitProvider.GITLAB:
      return `https://gitlab.com/${owner}/${repoSlug}/-/merge_requests/${pullRequestId}`;
    case GitProvider.BITBUCKET:
      return `https://bitbucket.org/${owner}/${repoSlug}/pull-requests/${pullRequestId}`;
    default:
      return undefined;
  }
};

export const getGitProviderCommitHashUrl = (
  id?: GitProvider,
  owner?: string,
  repoSlug?: string,
  commitHash?: string,
  pullRequestId?: number,
): string | undefined => {
  if (!id || !owner || !repoSlug || !commitHash) {
    return undefined;
  }

  switch (id) {
    case GitProvider.GITHUB:
    case GitProvider.GITHUB_APP:
      if (pullRequestId) {
        return `https://github.com/${owner}/${repoSlug}/pull/${pullRequestId}/commits/${commitHash}`;
      }
      return `https://github.com/${owner}/${repoSlug}/commit/${commitHash}`;
    case GitProvider.GITLAB:
      return `https://gitlab.com/${owner}/${repoSlug}/-/commit/${commitHash}`;
    case GitProvider.BITBUCKET:
      return `https://bitbucket.org/${owner}/${repoSlug}/commits/${commitHash}`;
    default:
      return undefined;
  }
};

export const getGitProviderConnectPath = (id: GitProvider): string | undefined => {
  switch (id) {
    case GitProvider.GITHUB:
      return '/users/auth/github';
    case GitProvider.GITLAB:
      return '/users/auth/gitlab';
    case GitProvider.BITBUCKET:
      return '/users/auth/bitbucket';
    case GitProvider.BITBUCKET_SERVER:
      return '/users/auth/bitbucket_server';
    default:
      return undefined;
  }
};

export const getGitProviderIdForTracking = (id: GitProviderId): string => {
  if (id !== GitProvider.CUSTOM) {
    return id.toLowerCase().replace(/-/g, '_');
  } else {
    return 'other';
  }
};

export default GitProvider;
